/**
 * Client for /api/anthropic/messages — sends Firebase ID token, never the Anthropic API key.
 */

import { getCurrentUser } from "./firebase";
import type { UsageAction } from "./subscription-plans";
import { getSimulatePlan, isSimulatingRegularUser } from "./test-as-user";
import { getUsageSession } from "./usage-session";

export const ANTHROPIC_PROXY_URL = "/api/anthropic/messages";

const SHARED_TOKEN_KEY = "bbg_firebase_id_token";
const SHARED_TOKEN_EXP_KEY = "bbg_firebase_id_token_exp";

let tokenLinkPromise: Promise<string> | null = null;
/** Set from literaWorker postMessage — workers have no Firebase auth or sessionStorage. */
let workerInjectedToken: string | null = null;

export function setWorkerFirebaseIdToken(token: string | null): void {
  workerInjectedToken = token;
}

/** Shared with LITRA Web Worker (no Firebase auth / usage session in workers). */
export function persistSharedIdToken(token: string, expiresInSec = 3600): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(SHARED_TOKEN_KEY, token);
    sessionStorage.setItem(
      SHARED_TOKEN_EXP_KEY,
      String(Date.now() + expiresInSec * 1000 - 60_000),
    );
  } catch {
    /* ignore */
  }
}

export function clearSharedIdToken(): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.removeItem(SHARED_TOKEN_KEY);
    sessionStorage.removeItem(SHARED_TOKEN_EXP_KEY);
  } catch {
    /* ignore */
  }
}

function readSharedIdToken(): string | null {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const token = sessionStorage.getItem(SHARED_TOKEN_KEY);
    const exp = Number(sessionStorage.getItem(SHARED_TOKEN_EXP_KEY) || 0);
    if (token && exp > Date.now()) return token;
    sessionStorage.removeItem(SHARED_TOKEN_KEY);
    sessionStorage.removeItem(SHARED_TOKEN_EXP_KEY);
  } catch {
    /* ignore */
  }
  return null;
}

async function resolveIdToken(): Promise<string> {
  if (workerInjectedToken) return workerInjectedToken;

  const shared = readSharedIdToken();
  if (shared) return shared;

  const current = await getCurrentUser();
  if (current) {
    const token = await current.getIdToken(true);
    persistSharedIdToken(token);
    return token;
  }

  throw new Error("Log ind for at fortsætte.");
}

/** Returns a Firebase ID token for /api/* routes. */
export async function getFirebaseIdToken(): Promise<string> {
  if (!tokenLinkPromise) {
    tokenLinkPromise = resolveIdToken().finally(() => {
      tokenLinkPromise = null;
    });
  }
  try {
    const token = await tokenLinkPromise;
    persistSharedIdToken(token);
    return token;
  } catch (error) {
    const detail = error instanceof Error ? error.message : String(error);
    if (/Log ind for at fortsætte/i.test(detail)) {
      throw error;
    }
    throw new Error(
      `Log ind for at bruge LITRA. Firebase-login fejlede (${detail}). Log ud og log ind igen med den rigtige adgangskode.`,
      { cause: error },
    );
  }
}

/** Call after admin login so LITRA does not wait on first message. */
export async function warmFirebaseIdToken(): Promise<void> {
  try {
    await getFirebaseIdToken();
  } catch {
    /* non-fatal — first LITRA send will surface error */
  }
}

export async function buildAnthropicProxyHeaders(
  usageAction: UsageAction,
): Promise<Record<string, string>> {
  const token = await getFirebaseIdToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
    "X-BBG-Usage-Action": usageAction,
  };

  const session = getUsageSession();
  if (session?.isAdmin && isSimulatingRegularUser()) {
    headers["X-BBG-Simulate-As-User"] = "1";
    const sim = getSimulatePlan();
    if (sim) headers["X-BBG-Simulate-Plan"] = sim;
  }

  return headers;
}

export type AnthropicProxyBody = {
  model?: string;
  max_tokens: number;
  stream?: boolean;
  system?: string;
  messages: Array<{ role: string; content: string }>;
};

export async function postAnthropicProxy(
  usageAction: UsageAction,
  body: AnthropicProxyBody,
  init?: { signal?: AbortSignal },
): Promise<Response> {
  const headers = await buildAnthropicProxyHeaders(usageAction);
  return fetch(ANTHROPIC_PROXY_URL, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
    signal: init?.signal,
  });
}

export function parseProxyErrorMessage(status: number, raw: string): string {
  try {
    const data = JSON.parse(raw) as {
      error?: string | { message?: string };
      usage?: { message?: string };
    };
    const nested =
      typeof data.error === "object" && data.error?.message
        ? data.error.message
        : undefined;
    const msg =
      data.usage?.message ||
      (typeof data.error === "string" ? data.error : nested) ||
      raw;
    if (status === 403) return msg;
    if (status === 401) {
      if (/invalid.*api.*key|authentication_error|x-api-key/i.test(msg)) {
        if (import.meta.env?.DEV) {
          return "Anthropic API 401: Tilføj ANTHROPIC_API_KEY i .env.local og kør npm run dev:api (port 3000).";
        }
        return "Anthropic API 401: Server ANTHROPIC_API_KEY is invalid or missing on Vercel.";
      }
      return "Log ind for at fortsætte.";
    }
    if (status === 500 && /Missing ANTHROPIC_API_KEY/i.test(msg)) {
      if (import.meta.env?.DEV) {
        return "Manglende ANTHROPIC_API_KEY — tilføj den i .env.local (ikke VITE_) og kør npm run dev:api.";
      }
      return "Missing ANTHROPIC_API_KEY — add it in Vercel Production env vars.";
    }
    if (status === 404 || /not found|failed to fetch/i.test(raw)) {
      return import.meta.env?.DEV
        ? "API-serveren svarer ikke lokalt. Kør npm run dev:api i et andet terminalvindue (port 3000), mens Vite kører på 5173."
        : "API-serveren svarer ikke. Kør vercel dev (port 3000) eller test på Vercel.";
    }
    return `Anthropic API ${status}: ${msg}`;
  } catch {
    if (/failed to fetch|network/i.test(raw)) {
      return import.meta.env?.DEV
        ? "API-serveren svarer ikke lokalt. Kør npm run dev:api i et andet terminalvindue (port 3000), mens Vite kører på 5173."
        : "API-serveren svarer ikke. Kør vercel dev (port 3000) eller test på Vercel.";
    }
    return `Anthropic API ${status}: ${raw}`;
  }
}
